import { FLAGS } from '@iheartjane/feature-flags';
import { CategoryTabs } from '@iheartjane/menu';
import type { LabeledOption, MenuTab } from '@iheartjane/types';
import { useEffect, useMemo, useRef } from 'react';
import type { ParamParseKey, PathMatch } from 'react-router';
import { matchPath, useMatch, useNavigation } from 'react-router';

import { CategorySelectorSkeleton } from '@jane/bloom/components/skeletonLoaders/categorySelectorSkeleton';
import { useFullDestination } from '@jane/bloom/hooks/navigation/useFullDestination';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import { useSkeletonLoader } from '@jane/bloom/hooks/useSkeletonLoader';
import { trackMenuTap } from '@jane/bloom/services/tracking/track';
import type { SearchFilterFacet } from '@jane/bloom/viewModels/productSearchFilters';

import { CategoryLink } from './categoryLink';

import styles from './categorySelector.module.css';

const CATEGORY_TO_SLUG: Partial<Record<MenuTab, string>> = {
  'all-products': 'shop-all',
  brand_specials: 'jane-gold',
  specials: 'deals',
};

const SLUG_TO_CATEGORY = Object.fromEntries(
  Object.entries(CATEGORY_TO_SLUG).map(([k, v]) => [v, k])
);

type CategorySelectorProps = {
  displayCategories: LabeledOption[];
};

// Prefer the in-flight navigation target over the current location so the
// highlight updates optimistically during a category change.
const useMatchesCurrentOrDestinationLocation = <TMatchPattern extends string>(
  matchPattern: TMatchPattern
): PathMatch<ParamParseKey<TMatchPattern>> | null => {
  const { location } = useNavigation();
  const currentPageMatch = useMatch<TMatchPattern>(matchPattern);
  const destinationPageMatch = matchPath<TMatchPattern>(
    matchPattern,
    location?.pathname ?? ''
  );

  return destinationPageMatch || currentPageMatch;
};

export function CategorySelector({ displayCategories }: CategorySelectorProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const { shouldShowFullSkeletonLoader } = useSkeletonLoader();
  const generatePath = useFullDestination();
  const { featureFlags, pinnedStore, shoppingPreferences, theme } =
    useRootLoaderData();

  const useCustomComponents = featureFlags?.[FLAGS.c2CustomComponents] ?? false;
  const primaryColor = theme?.bloom?.colors?.primary?.main ?? '#0E0E0E';

  const slugCategoryPage = useMatchesCurrentOrDestinationLocation(
    ':storeSlug/:category'
  );
  const localizedCategoryPage = useMatchesCurrentOrDestinationLocation(
    'store/:storeId/:category'
  );
  const categoryPage = useMatchesCurrentOrDestinationLocation(':category');

  const selectedCategory = useMemo(() => {
    const category =
      slugCategoryPage?.params.category ??
      localizedCategoryPage?.params.category ??
      categoryPage?.params.category;
    return SLUG_TO_CATEGORY[category ?? ''] ?? category;
  }, [categoryPage, localizedCategoryPage, slugCategoryPage]);

  const defaultCategory = displayCategories[0]?.value;

  const customLabels = useMemo(
    () =>
      displayCategories.reduce<Record<string, string>>((acc, category) => {
        acc[category.value] = category.label;
        return acc;
      }, {}),
    [displayCategories]
  );

  useEffect(() => {
    if (useCustomComponents) return;

    const scrollContainer = containerRef.current;
    const selectedElement = scrollContainer?.querySelector<HTMLElement>(
      '[data-selected="true"]'
    );

    if (selectedElement && scrollContainer) {
      const { offsetLeft: elementOffsetLeft, offsetWidth: elementWidth } =
        selectedElement;
      const { offsetWidth: containerWidth } = scrollContainer;

      const targetScrollLeft =
        elementOffsetLeft - (containerWidth - elementWidth) / 2;

      scrollContainer.scrollTo({
        behavior: 'smooth',
        left: targetScrollLeft,
      });
    }
  }, [useCustomComponents]);

  const getUrlString = (category: MenuTab) =>
    generatePath(`/${CATEGORY_TO_SLUG[category] ?? category}`);

  const handleClick = (category: MenuTab) => {
    trackMenuTap(
      {
        defaultOption: defaultCategory,
        menuOptionSelected: category,
        reservationMode: shoppingPreferences?.reservationMode,
      },
      featureFlags
    );
  };

  //This formatting is only needed for the old CategoryLink component
  const formattedDisplayCategories = displayCategories.map((category) => {
    const slug = CATEGORY_TO_SLUG[category.value as MenuTab];
    return {
      ...category,
      value:
        slug && category.value !== 'brand_specials' ? slug : category.value,
    };
  });

  return (
    <nav
      className={styles['categorySelector']}
      id="category-selector"
      ref={containerRef}
    >
      {shouldShowFullSkeletonLoader && !useCustomComponents ? (
        <CategorySelectorSkeleton />
      ) : useCustomComponents ? (
        <CategoryTabs
          categories={displayCategories.map(({ value }) => value as MenuTab)}
          customLabels={customLabels}
          getUrlString={getUrlString}
          onClick={handleClick}
          primaryColor={primaryColor}
          storeState={pinnedStore?.state}
          variant={theme?.bloom?.category_tabs?.style ?? 'icon'}
          selectedCategory={(selectedCategory ?? defaultCategory) as MenuTab}
        />
      ) : (
        formattedDisplayCategories.map((category: SearchFilterFacet) => (
          <CategoryLink
            key={category.value}
            reservationMode={shoppingPreferences?.reservationMode}
            defaultCategory={formattedDisplayCategories[0].value}
            category={category.value}
            label={category.label}
            selected={
              selectedCategory ===
              (SLUG_TO_CATEGORY[category.value as MenuTab] ?? category.value)
            }
          />
        ))
      )}
    </nav>
  );
}
